import React, { useState } from 'react';
import { IntakeZone, AUTO_CATEGORY_VALUE } from './IntakeZone';
import { ProgressTrack, ProgressStage } from './ProgressTrack';
import { IntakeRoute } from './hooks/useAutoDetectInput';
import { classifyCategories, Classification, CONFIDENCE_WARN_BELOW } from './hooks/useCategoryClassifier';
import { parseCostHeader } from './hooks/useCostLedger';
import { emitCost } from './costEvents';

type SmartStep = 'input' | 'processing' | 'preview';

const BORDER_THIN = '1px solid #ddd';
const INPUT_BASE = {
  width: '100%',
  padding: '8px',
  border: BORDER_THIN,
  borderRadius: '6px',
  fontSize: '0.95rem',
} as const;

const URL_CONCURRENCY = 3;
const BRAND_VOICE_BATCH_SIZE = 8;
const FETCH_TIMEOUT_MS = 600_000; // 10 minutes

interface ExtractedProduct {
  _id: string;
  name?: string;
  brand?: string;
  sku?: string;
  specifications?: Record<string, any>;
  category?: string;
  [key: string]: any;
}

interface PreviewRow extends ExtractedProduct {
  category: string;
  confidence: number;
  include: boolean;
}

interface Props {
  apiBase: string;
  apiKey: string;
  categoryOptions: readonly string[];
  fileSizeCaps: {
    pdf: { maxBytes: number; label: string };
    csv: { maxBytes: number; label: string };
    image: { maxBytes: number; label: string };
  };
  onError: (message: string, level?: 'error' | 'warning') => void;
  onComplete: (products: any[]) => void;
}

export const SmartIntakeFlow: React.FC<Props> = ({
  apiBase,
  apiKey,
  categoryOptions,
  fileSizeCaps,
  onError,
  onComplete,
}) => {
  const [step, setStep] = useState<SmartStep>('input');
  const [statusMsg, setStatusMsg] = useState('');
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);

  const stage: ProgressStage = step === 'input' ? 'intake' : step === 'processing' ? 'process' : 'process';

  const apiHeaders = (json: boolean) => {
    const h: Record<string, string> = { 'x-api-key': apiKey };
    if (json) h['Content-Type'] = 'application/json';
    return h;
  };

  const callEndpoint = async (path: string, init: RequestInit, timeoutMs = FETCH_TIMEOUT_MS): Promise<any> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(`${apiBase}/${path}`, { ...init, signal: controller.signal });
      clearTimeout(timer);
      emitCost(parseCostHeader(res.headers));
      const text = await res.text();
      let json: any = null;
      try { json = JSON.parse(text); } catch {}
      if (!res.ok) throw new Error(json?.detail || json?.error || `${path} failed ${res.status}`);
      return json;
    } catch (err) {
      clearTimeout(timer);
      if ((err as Error).name === 'AbortError') throw new Error(`Request to ${path} timed out`);
      throw err;
    }
  };

  const extractFromRoute = async (route: IntakeRoute, preSelected: string): Promise<ExtractedProduct[]> => {
    const categoryForExtraction = preSelected === AUTO_CATEGORY_VALUE
      ? (categoryOptions[0] as string)
      : preSelected;
    const skip = preSelected === AUTO_CATEGORY_VALUE ? 'true' : 'false';

    if (route.kind === 'pdf') {
      setStatusMsg('Extracting products from PDF…');
      const fd = new FormData();
      fd.append('file', route.file, route.file.name);
      fd.append('category', categoryForExtraction);
      fd.append('skip_brand_voice', 'true');
      const r = await callEndpoint('extract-pdf-products', { method: 'POST', headers: apiHeaders(false), body: fd });
      return Array.isArray(r?.products) ? r.products : [];
    }

    if (route.kind === 'csv') {
      setStatusMsg('Parsing CSV…');
      const fd = new FormData();
      fd.append('file', route.file, route.file.name);
      fd.append('category', categoryForExtraction);
      fd.append('skip_brand_voice', skip);
      const r = await callEndpoint('parse-csv', { method: 'POST', headers: apiHeaders(false), body: fd });
      return Array.isArray(r?.products) ? r.products : [];
    }

    if (route.kind === 'image') {
      setStatusMsg('Analysing image…');
      const fd = new FormData();
      fd.append('file', route.file, route.file.name);
      fd.append('category', categoryForExtraction);
      fd.append('additional_text', '');
      fd.append('skip_brand_voice', skip);
      const r = await callEndpoint('parse-image', { method: 'POST', headers: apiHeaders(false), body: fd });
      return Array.isArray(r?.products) ? r.products : [];
    }

    if (route.kind === 'url') {
      setStatusMsg(`Scraping ${route.urls.length} URL${route.urls.length === 1 ? '' : 's'}…`);
      const collected: ExtractedProduct[] = [];
      const urls = [...route.urls];
      const runOne = async (url: string) => {
        try {
          const r = await callEndpoint('scrape-url', {
            method: 'POST',
            headers: apiHeaders(true),
            body: JSON.stringify({ url, category: categoryForExtraction, skip_brand_voice: skip === 'true' }),
          });
          const ps = Array.isArray(r?.products) ? r.products : [];
          collected.push(...ps);
        } catch (e) {
          onError(`Skipped ${url}: ${(e as Error).message}`, 'warning');
        }
      };
      // Simple concurrency pool
      const workers: Promise<void>[] = [];
      for (let i = 0; i < URL_CONCURRENCY && urls.length; i++) {
        workers.push((async () => {
          while (urls.length) {
            const next = urls.shift();
            if (next) await runOne(next);
          }
        })());
      }
      await Promise.all(workers);
      return collected;
    }

    if (route.kind === 'text') {
      setStatusMsg('Processing text…');
      const r = await callEndpoint('process-text', {
        method: 'POST',
        headers: apiHeaders(true),
        body: JSON.stringify({
          text: route.text,
          category: categoryForExtraction,
          skip_brand_voice: skip === 'true',
        }),
      });
      return Array.isArray(r?.products) ? r.products : [];
    }

    return [];
  };

  const handleSubmit = async (route: IntakeRoute, category: string) => {
    setStep('processing');
    try {
      const extracted = await extractFromRoute(route, category);
      if (!extracted.length) throw new Error('No products were detected.');

      const withIds: ExtractedProduct[] = extracted.map((p, i) => ({
        ...p,
        _id: p.sku ? `${p.sku}-${i}` : `p-${Date.now()}-${i}`,
      }));

      let classifications: Classification[];
      if (category === AUTO_CATEGORY_VALUE) {
        setStatusMsg(`Classifying ${withIds.length} product${withIds.length === 1 ? '' : 's'}…`);
        classifications = await classifyCategories(apiBase, apiKey, withIds);
      } else {
        classifications = withIds.map((_, i) => ({ index: i, category, confidence: 1.0 }));
      }

      const rows: PreviewRow[] = withIds.map((p, i) => {
        const c = classifications[i] || { category: '', confidence: 0, index: i };
        return {
          ...p,
          category: c.category || (category === AUTO_CATEGORY_VALUE ? (categoryOptions[0] as string) : category),
          confidence: c.confidence,
          include: true,
        };
      });
      setPreviewRows(rows);
      setStep('preview');
      setStatusMsg('');
    } catch (err) {
      onError((err as Error).message || 'Processing failed');
      setStep('input');
      setStatusMsg('');
    }
  };

  const generateBrandVoice = async () => {
    const selected = previewRows.filter((r) => r.include);
    if (!selected.length) {
      onError('Select at least one product to generate.', 'warning');
      return;
    }

    setStep('processing');
    setStatusMsg('Generating descriptions…');

    try {
      // Group by category so each brand-voice call receives products with a
      // uniform category (matches today's call shape exactly).
      const groups = new Map<string, PreviewRow[]>();
      selected.forEach((r) => {
        const key = r.category;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(r);
      });

      const finalProducts: any[] = [];
      let completed = 0;
      const total = selected.length;

      for (const [cat, rows] of Array.from(groups.entries())) {
        // Process in batches to keep each HTTP request short (avoids proxy timeout)
        for (let i = 0; i < rows.length; i += BRAND_VOICE_BATCH_SIZE) {
          const batch = rows.slice(i, i + BRAND_VOICE_BATCH_SIZE);
          setStatusMsg(`Generating descriptions… ${completed}/${total} products`);
          const stripped = batch.map(({ include, confidence, _id, ...rest }) => rest);
          const r = await callEndpoint('generate-brand-voice', {
            method: 'POST',
            headers: apiHeaders(true),
            body: JSON.stringify({ products: stripped, category: cat }),
          });
          const voiced = Array.isArray(r?.products) ? r.products : [];
          voiced.forEach((v: any, j: number) => {
            finalProducts.push({ ...v, category: cat, _id: batch[j]?._id });
          });
          completed += batch.length;
        }
      }

      onComplete(finalProducts);
    } catch (err) {
      onError((err as Error).message || 'Brand voice generation failed');
      setStep('preview');
      setStatusMsg('');
    }
  };

  const updateRowCategory = (id: string, category: string) => {
    setPreviewRows((prev) => prev.map((r) => (r._id === id ? { ...r, category, confidence: 1.0 } : r)));
  };

  const toggleRow = (id: string) => {
    setPreviewRows((prev) => prev.map((r) => (r._id === id ? { ...r, include: !r.include } : r)));
  };

  const cancelPreview = () => {
    setPreviewRows([]);
    setStep('input');
  };

  if (step === 'input') {
    return (
      <div>
        <ProgressTrack activeStage="intake" />
        <IntakeZone
          categoryOptions={categoryOptions}
          fileSizeCaps={fileSizeCaps}
          onSubmit={handleSubmit}
          onInvalidFile={(m) => onError(m, 'error')}
        />
      </div>
    );
  }

  if (step === 'processing') {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <ProgressTrack activeStage={stage} statusMessage={statusMsg || 'Processing…'} />
        <div style={{ fontSize: '3rem', margin: '1rem 0', animation: 'spin 2s linear infinite', display: 'inline-block' }} aria-hidden="true">⏳</div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        <p style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>
          Large files may take a few minutes
        </p>
      </div>
    );
  }

  // step === 'preview'
  const anyLowConfidence = previewRows.some((r) => r.include && r.confidence > 0 && r.confidence < CONFIDENCE_WARN_BELOW);
  const selectedCount = previewRows.filter((r) => r.include).length;

  return (
    <div>
      <ProgressTrack activeStage="process" statusMessage="Review and confirm before generating descriptions" />

      <div style={{ background: '#f3f4f6', borderRadius: '8px', padding: '1rem', marginBottom: '1rem' }}>
        <strong>{selectedCount}</strong> of {previewRows.length} product{previewRows.length === 1 ? '' : 's'} selected.
        {anyLowConfidence && (
          <div style={{ marginTop: '0.5rem', color: '#92400e', fontSize: '0.9rem' }}>
            ⚠️ Some categories have low confidence — review before generating.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
        {previewRows.map((row) => {
          const lowConf = row.confidence > 0 && row.confidence < CONFIDENCE_WARN_BELOW;
          return (
            <div
              key={row._id}
              style={{
                display: 'grid',
                gridTemplateColumns: '28px 2fr 1fr 1fr',
                gap: '0.75rem',
                alignItems: 'center',
                padding: '0.75rem',
                border: BORDER_THIN,
                borderRadius: '6px',
                background: row.include ? 'white' : '#f9fafb',
                opacity: row.include ? 1 : 0.55,
              }}
            >
              <input
                type="checkbox"
                checked={row.include}
                onChange={() => toggleRow(row._id)}
                aria-label={`Include ${row.name || 'product'}`}
              />
              <div>
                <div style={{ fontWeight: 500, color: '#2c3e50' }}>{row.name || '(unnamed)'}</div>
                <div style={{ fontSize: '0.85rem', color: '#7f8c8d' }}>
                  {[row.brand, row.sku].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div>
                <select
                  value={row.category}
                  onChange={(e) => updateRowCategory(row._id, e.target.value)}
                  aria-label="Category"
                  style={{
                    ...INPUT_BASE,
                    borderColor: lowConf ? '#fbbf24' : BORDER_THIN,
                    background: lowConf ? '#fffbeb' : 'white',
                  }}
                >
                  {categoryOptions.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div style={{ fontSize: '0.85rem', color: lowConf ? '#92400e' : '#7f8c8d', textAlign: 'right' }}>
                {row.confidence > 0 ? `Confidence: ${Math.round(row.confidence * 100)}%` : 'Manual'}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <button
          onClick={generateBrandVoice}
          disabled={!selectedCount}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderRadius: '6px',
            background: selectedCount ? '#27ae60' : '#bdc3c7',
            color: '#fff',
            cursor: selectedCount ? 'pointer' : 'not-allowed',
            fontWeight: 600,
            fontSize: '1rem',
          }}
        >
          Generate descriptions →
        </button>
        <button
          onClick={cancelPreview}
          style={{
            padding: '12px 24px',
            border: BORDER_THIN,
            borderRadius: '6px',
            background: 'white',
            color: '#2c3e50',
            cursor: 'pointer',
            fontWeight: 500,
            fontSize: '1rem',
          }}
        >
          Cancel — start over
        </button>
      </div>
    </div>
  );
};
